import React, { useEffect, useMemo, useState } from 'react';
import { addWeeks, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns';
import { TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Modal, Button, StatusBanner } from '../ui';
import { useAvailabilityCRUD, describeRangeResult, SERIES_HORIZON_WEEKS } from '../../hooks/useAvailabilityCRUD';
import { apiErrorMessage } from '../../utils/apiError';

type Preset = 'week' | 'month' | 'future' | 'custom';

interface ClearAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The date the calendar is currently showing — drives the week/month presets. */
  currentDate: Date;
}

const iso = (d: Date) => format(d, 'yyyy-MM-dd');

/**
 * Bulk "clear my availability" — the answer to "I set up recurring availability
 * and now have to delete each block one by one". Removes every open slot in a
 * date range in one call; booked time and breaks are always kept.
 */
const ClearAvailabilityModal: React.FC<ClearAvailabilityModalProps> = ({ isOpen, onClose, currentDate }) => {
  const { deleteAvailabilityRange } = useAvailabilityCRUD();

  const today = useMemo(() => new Date(), []);
  const [preset, setPreset] = useState<Preset>('week');
  const [customStart, setCustomStart] = useState(iso(today));
  const [customEnd, setCustomEnd] = useState(iso(addWeeks(today, 4)));
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPreset('week');
      setAcknowledged(false);
      setError(null);
    }
  }, [isOpen]);

  const range = useMemo((): { start: string; end: string } => {
    switch (preset) {
      case 'week':
        return {
          start: iso(startOfWeek(currentDate, { weekStartsOn: 1 })),
          end: iso(endOfWeek(currentDate, { weekStartsOn: 1 })),
        };
      case 'month':
        return { start: iso(startOfMonth(currentDate)), end: iso(endOfMonth(currentDate)) };
      case 'future':
        return { start: iso(today), end: iso(addWeeks(today, SERIES_HORIZON_WEEKS * 2)) };
      case 'custom':
      default:
        return { start: customStart, end: customEnd };
    }
  }, [preset, currentDate, today, customStart, customEnd]);

  const rangeValid = !!range.start && !!range.end && range.start <= range.end;

  const presets: { value: Preset; title: string; detail: string }[] = [
    {
      value: 'week',
      title: 'This week',
      detail: `${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d')} – ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}`,
    },
    {
      value: 'month',
      title: format(currentDate, 'MMMM yyyy'),
      detail: 'Every open slot in this calendar month.',
    },
    {
      value: 'future',
      title: 'All my availability from today onward',
      detail: 'Removes everything you have set up, including recurring weeks, for the next 12 months.',
    },
    {
      value: 'custom',
      title: 'A date range I choose',
      detail: 'Pick the first and last day to clear.',
    },
  ];

  const handleClear = async () => {
    if (!rangeValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await deleteAvailabilityRange({ start_date: range.start, end_date: range.end });
      toast.success(describeRangeResult(result));
      onClose();
    } catch (e: any) {
      setError(apiErrorMessage(e, 'Failed to clear availability'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!submitting) onClose(); }}
      title="Clear availability"
      description="Remove open availability in bulk. Booked time and breaks are always kept."
      size="md"
      footer={(
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="danger"
            data-guide="clear-submit"
            onClick={handleClear}
            isLoading={submitting}
            disabled={submitting || !rangeValid || !acknowledged}
          >
            <TrashIcon className="h-4 w-4 mr-1.5" />
            Clear availability
          </Button>
        </div>
      )}
    >
      <div className="space-y-4">
        {error && <StatusBanner variant="error" title="Failed" message={error} />}

        <div className="space-y-2" role="radiogroup" aria-label="Range to clear" data-guide="clear-presets">
          {presets.map(p => (
            <label
              key={p.value}
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                preset === p.value
                  ? 'border-clinic-accent bg-clinic-accent/5'
                  : 'border-clinic-primary-lighter hover:bg-clinic-cream/50'
              }`}
            >
              <input
                type="radio"
                name="clear-preset"
                value={p.value}
                checked={preset === p.value}
                onChange={() => setPreset(p.value)}
                disabled={submitting}
                className="mt-1 h-4 w-4 text-clinic-accent border-clinic-primary-light focus:ring-clinic-accent/30"
              />
              <span>
                <span className="block text-sm font-medium text-clinic-dark">{p.title}</span>
                <span className="block text-xs text-clinic-muted">{p.detail}</span>
              </span>
            </label>
          ))}
        </div>

        {preset === 'custom' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="clear-start" className="block text-xs font-medium text-clinic-dark mb-1">From</label>
              <input
                id="clear-start"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="w-full px-3 py-2 border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
                disabled={submitting}
              />
            </div>
            <div>
              <label htmlFor="clear-end" className="block text-xs font-medium text-clinic-dark mb-1">To</label>
              <input
                id="clear-end"
                type="date"
                value={customEnd}
                min={customStart}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="w-full px-3 py-2 border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
                disabled={submitting}
              />
            </div>
            {!rangeValid && (
              <p className="col-span-2 text-xs text-clinic-accent">The end date must be on or after the start date.</p>
            )}
          </div>
        )}

        <div className="p-3 bg-clinic-cream/50 border border-clinic-primary-lighter rounded-lg text-sm text-clinic-dark">
          Clearing <strong>{format(new Date(`${range.start}T00:00:00`), 'MMM d, yyyy')}</strong> to{' '}
          <strong>{format(new Date(`${range.end}T00:00:00`), 'MMM d, yyyy')}</strong>.
          Any slot with a booking stays exactly as it is.
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            disabled={submitting}
            className="mt-0.5 h-4 w-4 text-clinic-accent rounded border-clinic-primary-light focus:ring-clinic-accent/30"
          />
          <span className="text-sm text-clinic-dark">
            I understand this removes all open availability in this range and can't be undone.
          </span>
        </label>
      </div>
    </Modal>
  );
};

export default ClearAvailabilityModal;
